import React, { useCallback, useEffect, useState } from 'react';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import { exportTicketsCsv, listTickets, respondToTicket, updateTicketStatus } from '../services/support';

const ISSUE_TYPE_LABELS = {
  system_bug: 'System bug',
  how_to: 'How-to guidance',
  class_content: 'Class or content issue',
  account_support: 'Account support',
  billing_support: 'Billing support',
  general_support: 'General support',
};

const IMPACT_LABELS = {
  blocker: 'Blocker - cannot proceed',
  high: 'High impact',
  medium: 'Medium impact',
  low: 'Low impact',
};

const FREQUENCY_LABELS = {
  always: 'Every time',
  intermittent: 'Sometimes',
  once: 'One-off',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const statusChipColor = {
  open: 'info',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const impactChipColor = {
  blocker: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ q: '', status: '' });
  const [drafts, setDrafts] = useState({});

  const loadTickets = useCallback(async (options) => {
    setLoading(true);
    setError('');
    try {
      const response = await listTickets({
        q: options?.q || undefined,
        status: options?.status || undefined,
      });
      setTickets(response?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setTickets([]);
      } else {
        const message = err?.response?.data?.error || 'Failed to load tickets';
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleResolve = async (id) => {
    try {
      const updated = await updateTicketStatus(id, 'resolved');
      setTickets((prev) => prev.map((ticket) => (ticket._id === id ? updated : ticket)));
      toast.success('Ticket marked as resolved');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update ticket');
    }
  };

  const handleResponseChange = (id, value) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const handleSendResponse = async (id) => {
    const message = drafts[id]?.trim();
    if (!message) return;
    try {
      const updated = await respondToTicket(id, message);
      setTickets((prev) => prev.map((ticket) => (ticket._id === id ? updated : ticket)));
      setDrafts((prev) => ({ ...prev, [id]: '' }));
      toast.success('Response sent');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send response');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportTicketsCsv({
        q: filters.q || undefined,
        status: filters.status || undefined,
      });
      const file = new Blob([blob], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `support_tickets_${Date.now()}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to download CSV');
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const environmentLabel = (structured) => {
    const env = structured?.environment || {};
    return [env.device, env.operatingSystem, env.browser, env.connection]
      .filter(Boolean)
      .join(' / ') || '-';
  };

  const selfHelpLabel = (structured) => (structured?.selfHelpTried || []).join(', ') || '-';

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                label="Search"
                placeholder="Search tickets"
                value={filters.q}
                onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="support-status-label">Status</InputLabel>
                <Select
                  labelId="support-status-label"
                  label="Status"
                  value={filters.status}
                  onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <MenuItem value="">
                    <em>All statuses</em>
                  </MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <AdminActionGroup>
                <Button
                  variant="contained"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={() => loadTickets(filters)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Apply filters'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={handleExport}
                >
                  Download CSV
                </Button>
              </AdminActionGroup>
            </Stack>
          </CardContent>
          {loading && <LinearProgress />}
        </Card>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {tickets.length === 0 && !loading ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No support tickets match the current filters.
            </Typography>
          </Paper>
        ) : (
          tickets.map((ticket) => {
            const structured = ticket.structured || {};
            const issueLabel = ISSUE_TYPE_LABELS[structured.issueType] || ISSUE_TYPE_LABELS.general_support;
            const impactLabel = IMPACT_LABELS[structured.impactLevel] || IMPACT_LABELS.medium;
            const frequencyLabel = FREQUENCY_LABELS[structured.frequency] || FREQUENCY_LABELS.intermittent;
            const statusLabel = STATUS_LABELS[ticket.status] || ticket.status;
            const responses = ticket.responses || [];

            return (
              <Card key={ticket._id} variant="outlined">
                <CardHeader
                  title={
                    <Stack spacing={1}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {ticket.subject}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          color={statusChipColor[ticket.status] || 'default'}
                          label={statusLabel}
                        />
                        <Chip size="small" color="info" label={issueLabel} />
                        <Chip
                          size="small"
                          color={impactChipColor[structured.impactLevel] || 'default'}
                          label={impactLabel}
                        />
                        <Chip size="small" variant="outlined" label={frequencyLabel} />
                        {structured.isBlocking && <Chip size="small" color="error" label="Blocking" />}
                      </Stack>
                    </Stack>
                  }
                  subheader={`Submitted ${formatDateTime(ticket.createdAt)}`}
                />
                <Divider />
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>From:</strong> {ticket.name || 'User'} ({ticket.email}) - {ticket.role || 'N/A'}
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ typography: 'body2' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Area
                        </Typography>
                        <Typography>{structured.featureArea || '-'}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Environment
                        </Typography>
                        <Typography>{environmentLabel(structured)}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Self-help tried
                        </Typography>
                        <Typography>{selfHelpLabel(structured)}</Typography>
                      </Box>
                    </Stack>

                    {structured.stepsToReproduce && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Steps to reproduce
                        </Typography>
                        <Typography variant="body2">{structured.stepsToReproduce}</Typography>
                      </Paper>
                    )}

                    {structured.expectedBehavior && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Expected behaviour
                        </Typography>
                        <Typography variant="body2">{structured.expectedBehavior}</Typography>
                      </Paper>
                    )}

                    {structured.actualBehavior && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Actual behaviour
                        </Typography>
                        <Typography variant="body2">{structured.actualBehavior}</Typography>
                      </Paper>
                    )}

                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        User message
                      </Typography>
                      <Typography variant="body2">{ticket.message}</Typography>
                    </Paper>

                    {responses.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                          Responses
                        </Typography>
                        <Stack spacing={1}>
                          {responses.map((response, index) => (
                            <Typography key={index} variant="body2">
                              <em>{formatDateTime(response.at)}:</em> {response.message}
                            </Typography>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </CardContent>

                {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                  <>
                    <Divider />
                    <CardContent>
                      <TextField
                        label="Respond"
                        placeholder="Write your response here..."
                        value={drafts[ticket._id] || ''}
                        onChange={(event) => handleResponseChange(ticket._id, event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                      />
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', gap: 1.5, px: 3, pb: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={<SendRoundedIcon />}
                        onClick={() => handleSendResponse(ticket._id)}
                        disabled={!drafts[ticket._id]?.trim()}
                      >
                        Send response
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => handleResolve(ticket._id)}
                      >
                        Mark as resolved
                      </Button>
                    </CardActions>
                  </>
                )}
              </Card>
            );
          })
        )}
      </Stack>
    </DashboardLayout>
  );
};

export default Support;
